import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDiagnosisAvailable } from '@/lib/diagnosis';
import {
  ShieldCheck, Tag, Sprout, CalendarDays, Landmark, ScanSearch, MapPin, CloudSun,
  Package, ShoppingCart, BadgePercent, Users, Store, Truck, TrendingUp, BarChart3,
  Factory, Wheat,
} from 'lucide-react';

/**
 * The big coloured tiles on every home screen: each tile is a solid
 * coloured button with a white icon and label, no frame around it, two
 * per row. The tiles are the main way into the app, so each role gets
 * the screens that matter to it.
 *
 * `soon` marks a screen that isn't live yet; the tile still opens it
 * (the page explains), with a white "Coming soon" label underneath.
 * Crop Disease Check drops the label once the server has a Plantix key.
 *
 * The colours are a shade darker than the old icon squares so white
 * text stays readable on them (amber is the exception, as in the design).
 */
const COLORS = {
  yellow: 'bg-[#E59400]',
  green: 'bg-[#388E3C]',
  leaf: 'bg-[#2E7D32]',
  blue: 'bg-[#1E88E5]',
  purple: 'bg-[#7B1FA2]',
  orange: 'bg-[#F4511E]',
  teal: 'bg-[#00897B]',
  sky: 'bg-[#0288D1]',
};

const TILE = {
  approved:     { icon: ShieldCheck,  color: COLORS.yellow, path: '/verify' },
  bestPrices:   { icon: Tag,          color: COLORS.green,  path: '/marketplace' },
  nutrition:    { icon: Sprout,       color: COLORS.leaf,   path: '/nutrition' },
  spraying:     { icon: CalendarDays, color: COLORS.blue,   path: '/spray' },
  government:   { icon: Landmark,     color: COLORS.purple, path: '/government' },
  // "Coming soon" until Plantix is switched on (useDiagnosisAvailable).
  disease:      { icon: ScanSearch,   color: COLORS.orange, path: '/diagnosis', soon: 'diagnosis' },
  myFarm:       { icon: MapPin,       color: COLORS.teal,   path: '/farm' },
  weather:      { icon: CloudSun,     color: COLORS.sky,    path: '/weather' },
  myStock:      { icon: Package,      color: COLORS.green,  path: '/inventory' },
  marketplace:  { icon: ShoppingCart, color: COLORS.yellow, path: '/marketplace' },
  dealsIn:      { icon: Tag,          color: COLORS.orange, path: '/deals?tab=received' },
  // A shop's own deal starts from his distributor's deals (pass on / edit,
  // PR 72), with "Create a new deal" there too (PR 74).
  createDeal:   { icon: BadgePercent, color: COLORS.blue,   path: '/deals?tab=received&start=1' },
  deals:        { icon: BadgePercent, color: COLORS.orange, path: '/deals' },
  customers:    { icon: Users,        color: COLORS.teal,   path: '/customers' },
  distributors: { icon: Truck,        color: COLORS.leaf,   path: '/distributors' },
  shopkeepers:  { icon: Store,        color: COLORS.teal,   path: '/shopkeepers' },
  // Every manufacturer on the app (beta feedback), to find and connect.
  manufacturers: { icon: Factory,     color: COLORS.leaf,   path: '/directory/manufacturer' },
  myProducts:   { icon: Package,      color: COLORS.green,  path: '/products' },
  // Demo numbers until real regional forecasts exist (beta feedback).
  forecasts:    { icon: TrendingUp,   color: COLORS.sky,    path: '/forecasts', soon: true },
  analytics:    { icon: BarChart3,    color: COLORS.blue,   path: '/analytics' },
  farmProduce:  { icon: Wheat,        color: COLORS.leaf,   path: '/produce' },
};

export const ROLE_TILES = {
  farmer: ['approved', 'bestPrices', 'nutrition', 'spraying', 'government', 'disease', 'myFarm', 'weather', 'farmProduce'],
  // No Farm produce tile: the yellow button above does it (PR 74).
  shopkeeper: ['myStock', 'marketplace', 'dealsIn', 'createDeal', 'customers', 'distributors', 'forecasts', 'government'],
  // No Farm produce tile: the yellow button above does it (PR 73).
  distributor: ['myStock', 'marketplace', 'shopkeepers', 'manufacturers', 'deals', 'customers', 'forecasts', 'government'],
  manufacturer: ['myProducts', 'marketplace', 'distributors', 'deals', 'analytics', 'forecasts', 'customers', 'government', 'farmProduce'],
};

// Screens the home search can jump to: this role's tiles plus a few
// screens every role has. Several labels may lead to the same screen
// (e.g. "Find the Best Prices" and "Marketplace"); the search shows one.
const COMMON_SCREENS = [
  { key: 'marketplace', labelKey: 'nav.marketplace', path: '/marketplace' },
  { key: 'deals', labelKey: 'nav.deals', path: '/deals' },
  { key: 'support', labelKey: 'supportChat.title', path: '/support/chat' },
  { key: 'notifications', labelKey: 'nav.notifications', path: '/notifications' },
  { key: 'profile', labelKey: 'nav.profile', path: '/profile' },
];

export function roleScreens(role) {
  const keys = ROLE_TILES[role] || ROLE_TILES.farmer;
  return [
    ...keys.map((key) => ({ key, labelKey: `homeTiles.${key}`, path: TILE[key].path })),
    ...COMMON_SCREENS,
  ];
}

export default function HomeTiles({ role }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const keys = ROLE_TILES[role] || ROLE_TILES.farmer;
  const diagnosisLive = useDiagnosisAvailable();

  return (
    <div className="grid grid-cols-2 gap-4 mb-5" data-testid="home-tiles">
      {keys.map((key, index) => {
        const tile = TILE[key];
        // An odd last tile (Farm produce, PR 67) spans the row.
        const wide = keys.length % 2 === 1 && index === keys.length - 1;
        const Icon = tile.icon;
        const soon = tile.soon === 'diagnosis' ? diagnosisLive !== true : tile.soon;
        return (
          <button
            key={key}
            type="button"
            onClick={() => navigate(tile.path)}
            className={`${wide ? 'col-span-2 flex-row justify-center min-h-[96px] gap-4 ' : 'flex-col justify-center min-h-[148px] gap-2.5 '}${tile.color} rounded-3xl shadow-md px-3 py-4 flex items-center text-center min-w-0 active:scale-[0.97] transition-transform focus:outline-none focus-visible:ring-4 focus-visible:ring-farm-forest/40`}
            data-testid={`home-tile-${key}`}
          >
            <Icon className="w-14 h-14 shrink-0 text-white drop-shadow-sm" strokeWidth={2} aria-hidden="true" />
            <span className="flex flex-col items-center gap-1.5 min-w-0">
              <span className="font-heading font-extrabold text-lg leading-tight text-white break-words [text-shadow:0_1px_2px_rgba(0,0,0,0.25)]">
                {t(`homeTiles.${key}`)}
              </span>
              {soon && (
                <span className="bg-white text-farm-forest text-[10px] font-extrabold uppercase tracking-wide px-2.5 py-0.5 rounded-full">
                  {t('common.comingSoon.title')}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}
